import React, { useContext } from 'react';
import { ThemeProvider } from 'emotion-theming';
import { genThemedStyled, Theme, ThemeContext } from '../../utilities/theme';
import {
  ColorCodes,
  colorsForTheme,
  FUTURO as C,
} from '../../foundations/color';
import S from '../../foundations/size';

/**
 * A futuro dropdown mainly used for items in headers
 *
 * @argument children React.ReactNodes -
 * @argument isActive boolean - true/false value that displays the dropdown. Usually comes from a button click.
 * @argument anchor string - ['center', 'right'] - dictates where the triangle anchor is placed.
 * @argument anchorColor ColorCodes - changes the color of the anchor
 *
 */

export type HelpDropdownProps = {
  children?: React.ReactNode;
  isActive?: boolean;
  anchor?: 'center' | 'right';
  anchorColor?: ColorCodes;
};

const getTheme = (theme: Theme): themeStyles => {
  const colors = colorsForTheme(theme);
  const colorThemes = {
    backgroundColor: colors.SWAN,
  };
  return colorThemes;
};

type themeStyles = {
  backgroundColor: ColorCodes;
};

const ThemeStyled = genThemedStyled<themeStyles>();

const DropdownWrapper = ThemeStyled.div<{
  isActive: boolean;
  theme: themeStyles;
  anchor: string;
}>(({ isActive, theme, anchor }) => ({
  position: 'absolute',
  display: isActive ? 'flex' : 'none',
  flexDirection: 'column',
  left: anchor === 'center' ? 'unset' : '-18rem',
  zIndex: 30,
  gap: S.S,
  alignItems: 'flex-start',
  width: '21rem',
  backgroundColor: theme.backgroundColor,
  borderRadius: S.XXXS,
  boxShadow: `0px ${S.XS} ${S.XL} rgba(0, 0, 0, 0.08)`,
  top: S.XXXL,
  cursor: 'initial',
}));

const TriangleWrapper = ThemeStyled.div<{
  isActive: boolean;
  anchorColor: ColorCodes;
}>(({ isActive, anchorColor }) => ({
  zIndex: 1,
  position: 'absolute',
  display: isActive ? 'flex' : 'none',
  flexDirection: 'column',
  gap: S.S,
  alignItems: 'flex-start',
  '::after': {
    content: '""',
    position: 'absolute',
    top: S.S,
    left: `-${S.XS}`,
    borderRadius: S.XXXS,
    transform: 'rotate(-45deg)',
    boxShadow: `0px 1px ${S.XXXS} rgba(0, 0, 0, 0.14)`,
    border: `${S.XS} solid black`,
    borderTopColor: anchorColor,
    borderRightColor: anchorColor,
    borderBottomColor: 'transparent',
    borderLeftColor: 'transparent',
  },
}));

export const HelpDropdown = (props: HelpDropdownProps) => {
  const {
    children,
    isActive = false,
    anchor = 'center',
    anchorColor = C.LIGHT.SWAN,
  } = props;
  const themeStyle = getTheme(useContext(ThemeContext));
  return (
    <ThemeProvider theme={themeStyle}>
      <DropdownWrapper isActive={isActive} anchor={anchor}>
        {children}
      </DropdownWrapper>
      <TriangleWrapper isActive={isActive} anchorColor={anchorColor} />
    </ThemeProvider>
  );
};

export default HelpDropdown;
